/**
 * Connections Tab
 *
 * Manage per-user OAuth connections to external APIs.
 * Supports: list, test, reconnect (via OAuth), delete.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  PlayArrow as TestIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';

import apiManagerApi, { apiOAuthApi } from './api';

interface Connection {
  connection_id: string;
  api_id: string;
  user_id: string;
  status: string;
  expires_at: string | null;
  created_at: string;
  updated_at?: string;
}

interface ApiInfo {
  api_id: string;
  api_name: string;
  provider: string;
  auth_type: string;
}

export default function ConnectionsTab() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [apis, setApis] = useState<Record<string, ApiInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { success: boolean; message: string }>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [connResp, apiResp] = await Promise.all([
        apiManagerApi.listConnections(),
        apiManagerApi.listApis(),
      ]);
      setConnections(connResp.data.connections || []);

      // Build a lookup of api_id -> api info
      const apiMap: Record<string, ApiInfo> = {};
      for (const a of (apiResp.data.apis || [])) {
        apiMap[a.api_id] = { api_id: a.api_id, api_name: a.api_name, provider: a.provider, auth_type: a.auth_type };
      }
      setApis(apiMap);
    } catch (err: any) {
      console.error('Failed to load connections:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load connections');
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!confirm('Are you sure you want to delete this connection?')) return;
    try {
      await apiManagerApi.deleteConnection(connectionId);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete connection');
    }
  };

  const handleTestConnection = async (connectionId: string) => {
    setTestingId(connectionId);
    setTestResult((prev) => ({ ...prev, [connectionId]: undefined as any }));
    try {
      const resp = await apiManagerApi.testConnection(connectionId);
      setTestResult((prev) => ({
        ...prev,
        [connectionId]: { success: resp.data?.success ?? true, message: resp.data?.message || 'Connection OK' },
      }));
    } catch (err: any) {
      setTestResult((prev) => ({
        ...prev,
        [connectionId]: { success: false, message: err.response?.data?.error || err.message || 'Test failed' },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const isExpired = (conn: Connection): boolean => {
    if (!conn.expires_at) return false;
    return new Date(conn.expires_at).getTime() < Date.now();
  };

  const getStatusColor = (conn: Connection): any => {
    if (isExpired(conn)) return 'error';
    switch (conn.status) {
      case 'connected': return 'success';
      case 'expired': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (conn: Connection): string => {
    if (isExpired(conn)) return 'EXPIRED';
    return (conn.status || 'unknown').toUpperCase();
  };

  return (
    <Box sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Connections</Typography>
        <Tooltip title="Refresh list">
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && connections.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LinkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No User Connections
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            Per-user OAuth connections appear here when you connect your external accounts
            (John Deere, agrirouter, etc.) through the API Setup chat.
          </Typography>
        </Box>
      )}

      {/* Connections Table */}
      {!loading && connections.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>API</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Connected</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((conn) => {
                const apiInfo = apis[conn.api_id];
                const expired = isExpired(conn);
                const tr = testResult[conn.connection_id];
                return (
                  <TableRow key={conn.connection_id} sx={{ bgcolor: expired ? 'error.50' : undefined }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {apiInfo?.api_name || conn.api_id}
                      </Typography>
                      {apiInfo && (
                        <Typography variant="caption" color="text.secondary">
                          {apiInfo.provider}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(conn)}
                        size="small"
                        color={getStatusColor(conn)}
                        icon={expired ? <WarningIcon /> : conn.status === 'connected' ? <CheckCircleIcon /> : undefined}
                      />
                      {tr && (
                        <Typography variant="caption" display="block" color={tr.success ? 'success.main' : 'error.main'} sx={{ mt: 0.5 }}>
                          {tr.message}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {conn.expires_at ? (
                        <Tooltip title={new Date(conn.expires_at).toISOString()}>
                          <Typography variant="body2" color={expired ? 'error.main' : 'text.secondary'}>
                            {expired ? 'Expired ' : ''}{new Date(conn.expires_at).toLocaleDateString()}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">No expiry</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(conn.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Test connection">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleTestConnection(conn.connection_id)}
                            disabled={testingId === conn.connection_id}
                          >
                            {testingId === conn.connection_id ? <CircularProgress size={16} /> : <TestIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete connection">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteConnection(conn.connection_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
